import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { translationPropType } from '@eb/i18n';
import isEmpty from 'lodash/isEmpty';

import { formValidate } from '@eb/eds-utils';
import gettext from '@eb/gettext';
import { SocialLogin } from '@eb/social-login-facebook';

import FormSubmitButton from '../FormSubmitButton';
import EmailField from '../EmailField';

import { lookUpValidate as validate } from '../utils';
import { LOOK_UP_FORM } from '../../constants';
import { PASSWORD_TOKEN_EXPIRED_MESSAGE } from '../../constants/messages';
import { notifyParent } from '../../utils';

const ContinueAsGuestLink = () => {
    const onClick = () => notifyParent('checkoutContinueAsGuest');

    return (
        <div className="eds-l-mar-vert-14 eds-text--center">
            <a
                href="#"
                data-spec="checkout-login-continue-as-guest--link"
                onClick={onClick}
            >
                {gettext('Continue as guest')}
            </a>
        </div>
    );
};

class LookUpForm extends React.PureComponent {
    static propTypes = {
        // Redux changes the `onSubmit` prop passed to handle submit
        handleSubmit: PropTypes.func.isRequired,
        buttonCTA: translationPropType,
        showNotification: PropTypes.func,
        clearNotification: PropTypes.func,
        shouldAnimate: PropTypes.bool,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
            apple: PropTypes.object,
        }),
        onSocialConnectVerified: PropTypes.func,
        onSocialConnectNotAuthorized: PropTypes.func,
        onSocialProviderClick: PropTypes.func,
        onGetStartedClick: PropTypes.func,
        onTermsOfServiceClick: PropTypes.func,
        onComunityGuidelinesClick: PropTypes.func,
        onPrivacyPolicyClick: PropTypes.func,

        /* Props Provided Via ReduxForm */
        submitSucceeded: PropTypes.bool,
        submitFailed: PropTypes.bool,
        submitting: PropTypes.bool,
        fp: PropTypes.bool,
    };

    state = {
        isDisabled: true,
    };

    componentDidMount() {
        const { trackOnPageLoad, socialData } = this.props;

        //eslint-disable-next-line react/no-did-mount-set-state
        this.setState({ isDisabled: false });

        if (trackOnPageLoad && !isEmpty(socialData)) {
            trackOnPageLoad();
        }
    }

    componentWillReceiveProps({
        submitting,
        submitFailed,
        error,
        invalidPasswordResetToken,
    }) {
        const { showNotification, clearNotification } = this.props;

        /*If the form has an error and submit failed, attempt
        to display a notification*/
        if (submitFailed && error) {
            showNotification(error);
        }

        /*We want to limit only one error notification to
        appear per form submission if the user has dismissed it.
        So each time submitting is true, then reset the flag.*/
        if (submitting) {
            clearNotification();
        }

        if (
            !this.props.invalidPasswordResetToken &&
            invalidPasswordResetToken
        ) {
            showNotification(PASSWORD_TOKEN_EXPIRED_MESSAGE);
        }
    }

    componentWillUnmount() {
        const { clearNotification } = this.props;

        clearNotification && clearNotification();
    }

    render() {
        const {
            buttonCTA,
            fp,
            handleSubmit,
            hideSocialLogin,
            isCheckoutAuthenticationFlow,
            onComunityGuidelinesClick,
            onGetStartedClick,
            onPrivacyPolicyClick,
            onSocialConnectNotAuthorized,
            onSocialLogInUserAuthorized,
            onSocialConnectVerified,
            onSocialProviderClick,
            onTermsOfServiceClick,
            shouldAnimate,
            socialSSOSettings,
            submitFailed,
            submitting,
        } = this.props;

        const { isDisabled } = this.state;

        const emailFieldClasses = 'eds-l-mar-bot-6';

        const buttonClasses = classNames({
            'eds-fx--fade-in-up eds-fx--delay-1': shouldAnimate,
        });

        const isSubmitDisabled = submitting || isDisabled;

        const continueAsGuestLink = isCheckoutAuthenticationFlow && (
            <ContinueAsGuestLink />
        );

        let formComponent = (
            <SocialLogin
                onSocialConnectVerified={onSocialConnectVerified}
                onSocialConnectNotAuthorized={onSocialConnectNotAuthorized}
                onSocialLogInUserAuthorized={onSocialLogInUserAuthorized}
                socialSSOSettings={socialSSOSettings}
                onSocialProviderClick={onSocialProviderClick}
                onTermsOfServiceClick={onTermsOfServiceClick}
                onComunityGuidelinesClick={onComunityGuidelinesClick}
                onPrivacyPolicyClick={onPrivacyPolicyClick}
                fp={fp}
            >
                <form onSubmit={handleSubmit} noValidate="novalidate">
                    <EmailField
                        submitFailed={submitFailed}
                        buttonClasses={emailFieldClasses}
                        isStatic={true}
                    />
                    <FormSubmitButton
                        buttonClasses={buttonClasses}
                        submitDisabled={isSubmitDisabled}
                        buttonCTA={buttonCTA}
                        onClick={onGetStartedClick}
                    />
                    {continueAsGuestLink}
                </form>
            </SocialLogin>
        );

        if (hideSocialLogin) {
            formComponent = (
                <form onSubmit={handleSubmit} noValidate="novalidate">
                    <EmailField
                        submitFailed={submitFailed}
                        buttonClasses={emailFieldClasses}
                        isStatic={true}
                    />
                    <FormSubmitButton
                        buttonClasses={buttonClasses}
                        submitDisabled={isSubmitDisabled}
                        buttonCTA={buttonCTA}
                        onClick={onGetStartedClick}
                    />
                    {continueAsGuestLink}
                </form>
            );
        }

        return formComponent;
    }
}

export default formValidate({
    validate,
    form: LOOK_UP_FORM,
    destroyOnUnmount: false,
    touchOnBlur: false,
})(LookUpForm);
